import React, { ChangeEvent, FC, Fragment, HTMLAttributes, useState } from 'react';
import {
  Autocomplete,
  CircularProgress,
  TextField,
  Typography,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import { useDebounce } from 'use-debounce';

import { ProfileSuggestion, ProfileSuggestionFragment, useUserSuggestQuery } from 'api';
import { UserAvatar } from 'components';
import { PopupIcon, useAutoCompleteStyles } from 'components/Pickers';
import { useNotify } from 'hooks';

interface IProps {
  placeholder: string;
  onChange: (group: Partial<ProfileSuggestion>) => void;
  excludeIds: string[];
}

const SuggestProfile: FC<IProps> = ({ placeholder, onChange, excludeIds }) => {
  const autoCompleteClasses = useAutoCompleteStyles;
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const [prefix] = useDebounce(inputValue, 250);
  const { data, isLoading } = useUserSuggestQuery(
    { prefix },
    {
      onError: notify.queryError,
      enabled: !!prefix,
    }
  );

  const options = data?.suggest?.results ?? [];
  const filteredOptions = options.filter(
    (result: ProfileSuggestionFragment) => !excludeIds.includes(result.id)
  ) as ProfileSuggestionFragment[];

  function handleChange(event: ChangeEvent, nextValue: ProfileSuggestionFragment) {
    setInputValue('');
    onChange(nextValue);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    setInputValue(event?.target?.value || '');
  }

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={filteredOptions}
      loading={isLoading}
      onChange={handleChange}
      getOptionLabel={option => option.title}
      sx={autoCompleteClasses}
      inputValue={inputValue}
      value={null}
      popupIcon={<PopupIcon open={open} />}
      renderOption={(props: HTMLAttributes<HTMLLIElement>, option: ProfileSuggestionFragment) => (
        <Grid component="li" container alignItems="center" {...props}>
          <Grid marginRight={2}>
            <UserAvatar user={option} />
          </Grid>
          <Grid>
            <Typography>{option.title}</Typography>
          </Grid>
        </Grid>
      )}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          placeholder={placeholder}
          onChange={handleInputChange}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Fragment>
                {isLoading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export default SuggestProfile;
